/*
Company: Tiber Health Innovations
Initial Author: Bryan M Lenihan
Date Created: 2025-01-15

Version Table:
----------------------------------------
Version: 1.0
Author: Me
Description: Initial
Description: Initial
*/

/**
 * The UTMHandler class manages the extraction, storage, and access of UTM (Urchin Tracking Module)
 * codes used for tracking marketing and analytics data. It prioritizes UTM code extraction from
 * query strings, then cookies, and lastly, local storage, ensuring the highest precedence values
 * are stored and accessible.
 */
export default class UTMHandler {
    static allowedDomains = [];
    static api_url = "https://portal.tiberhealth.com/api/marketing/leadsource";

    static extract() {
        const utmCodes = this.extractUTMCodes();
        UTMHandler.storeUTMCodes(utmCodes);
        UTMHandler.getLeadSource();
    }

    static extractUTMCodes() {
        const utmCodes = {};

        // Local Storage (lowest precedence)
        Object.assign(utmCodes, UTMHandler.getUTMCodesFromLocalStorage());

        // Cookies (overrides Local Storage)
        Object.assign(utmCodes, UTMHandler.getUTMCodesFromCookies());

        // Query String (highest precedence, overrides both)
        Object.assign(utmCodes, UTMHandler.getUTMCodesFromQueryString());

        return utmCodes;
    }

    static isCaptureCode(key) {
        const acceptedValues = ['gclid', 'leadSource', 'leadDescription'].map(item => item.toLowerCase());
            return key.toLowerCase().startsWith('utm_') || acceptedValues.includes(key.toLowerCase());
    }

    static getUTMCodesFromQueryString() {
        const params = new URLSearchParams(window.location.search);
        const utmCodes = {};

        for (const [key, value] of params.entries()) {
            if (UTMHandler.isCaptureCode(key)) {
                utmCodes[key] = value;
            }
        }
        return utmCodes;
    }

    static getUTMCodesFromLocalStorage() {
        const utmCodes = {};
        const storageKeys = Object.keys(localStorage).filter(key => UTMHandler.isCaptureCode(key));
        for (const key of storageKeys) {
            const value = this.getLocalStorageValue(key);
            if (value != null) {
                utmCodes[key] = value;
            } else {
                localStorage.removeItem(key);
            }
        }

        return utmCodes;
    }

    static getLocalStorageValue(key) {
        const now = Date.now();
        try {
            const item = JSON.parse(localStorage.getItem(key));
            const expiry = item?.expiry ?? item?.expiryDate ?? 0;
            if (item && expiry > now) {
                return item.value; // Only include non-expired items
            } else {
                localStorage.removeItem(key); // Remove expired items
            }
        } catch {
            // Fallback: remove invalid JSON or corrupted data
            localStorage.removeItem(key);
        }

        return null;
    }

    static storeUTMCodes(utmCodes) {
        const expirationDays = 20; // Set expiration period
        const maxAge = expirationDays * 24 * 60 * 60; // Convert days to seconds

        for (const [key, value] of Object.entries(utmCodes)) {
            const expiryTimestamp = Date.now() + maxAge * 1000; // Convert seconds to milliseconds
            const dataWithExpiry = JSON.stringify({ value, expiry: expiryTimestamp });
            localStorage.setItem(key, dataWithExpiry);

            document.cookie = `${key}=${encodeURIComponent(value)}; path=/; secure; max-age=${maxAge}`;
        }
    }

    static getUTMCodesFromCookies() {
        const cookies = document.cookie.split(';');
        const utmCodes = {};

        cookies.forEach(cookie => {
            const [key, value] = cookie.trim().split('=');
            if (UTMHandler.isCaptureCode(key)) {
                utmCodes[key] = decodeURIComponent(value);
            }
        });

        return utmCodes;
    }

    static getUTMCodes(pascalCase = false, preserveFormat = false) {
        const utmLocalStorageCodes = UTMHandler.getUTMCodesFromLocalStorage();
        const utmCodes = {}

        Object.keys(utmLocalStorageCodes).forEach(key => {
            if (UTMHandler.isCaptureCode(key)) {
                let formattedKey = key;
                if (!preserveFormat) {
                    formattedKey = key.toLowerCase().split("_").map((word, idx) => (idx === 0 && !pascalCase)  ? word : word.charAt(0).toUpperCase() + word.slice(1)).join("");
                }
                const keyValue = utmLocalStorageCodes[key];
                if ((keyValue?.length ?? 0) > 0) {
                    utmCodes[formattedKey] = keyValue;
                }
            }
        });

        return utmCodes;
    }

    static getLeadSourceUrl() {
        const utmCodes = UTMHandler.getUTMCodes();
        const url = new URL(UTMHandler.api_url);

        for (const [key, value] of Object.entries(utmCodes)) {
            if (value != null && value !== '') {
                url.searchParams.set(key, value);
            }
        }

        return url.toString();
    }

    static getLeadSource() {
        const url = UTMHandler.getLeadSourceUrl();
        fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Unable to retrieve Lead Source and Description: ' + response.statusText);
                }
                return response.json();
            })
            .then(data => {
                if (data != null && data.leadSource != null) {
                    const utmCodes = {
                        leadSource: data.leadSource,
                        leadDescription: data.leadSourceDescription
                    };
                    UTMHandler.storeUTMCodes(utmCodes);
                }
            })
            .catch(error => {
                console.error('There was a problem with the fetch operation:', error.name, error.message, error.stack);
            })
            .finally(() => UTMHandler.runAfterDomReady(() =>   UTMHandler.appendUtmToLinks()));
    }

    static runAfterDomReady(callback) {
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", callback);
        } else {
            callback();
        }
    }

    /**
     * Accepts an array of domains, which may include wildcards like 
     * example.com, *.example.com, *example*
     * and sets those domains to be included in our whitelist. 
     */
    static setAllowedDomains(domains = []) {
        if (!Array.isArray(domains)) return;

        UTMHandler.allowedDomains = domains.filter(domain =>
            typeof domain === 'string' && domain.length > 0
        );

        UTMHandler.appendUtmToLinks();
    }

    static isDomainAllowed(hostname) {
        return UTMHandler.allowedDomains.some(pattern => {
            if (pattern.startsWith('*.')) {
                //Subdomain matching, e.g. *.example.com will match test.example.com but not example.com 
                const base = pattern.slice(2);
                return hostname.endsWith(`.${base}`);
            }
            else if (pattern.startsWith('*') && pattern.endsWith('*')) {
                //Substring matching, so that *example* matches all domains containing example 
                const base = pattern.slice(1, -1);
                return hostname.includes(base);
            }
            else if (pattern.startsWith('*')) {
                //Suffix matching, so that *example.com matches both example.com and test.example.com 
                const base = pattern.slice(1);
                return hostname.endsWith(base);
            }
            else if (pattern.endsWith('*')) {
                //Prefix matching, so that example* matches example.com, example.org, or example1234.com
                const base = pattern.slice(0, -1);
                return hostname.startsWith(base);
            }
            else {
                //Exact match
                return hostname === pattern; 
            }
        });
    }

    static appendUtmToLinks() {
        if (UTMHandler.allowedDomains.length === 0) return;

        const utmParams = UTMHandler.getUTMCodes(false, true); //don't use pascal case, use format given 
        const utmQueryParams = UTMHandler.truncateQueryParams(utmParams);
        if (!utmQueryParams || [...utmQueryParams].length === 0) return; 

        const hrefAnchors = document.querySelectorAll('a[href]');

        hrefAnchors.forEach(anchor => {
            try {
                const url = new URL(anchor.href);
                const hostname = url.hostname;

                if (!UTMHandler.isDomainAllowed(hostname)) return;

                for (const key of [...url.searchParams.keys()]) {
                    if (UTMHandler.isCaptureCode(key)) {
                        url.searchParams.delete(key);
                    }
                }

                for (const [key, value] of utmQueryParams.entries()) {
                    url.searchParams.set(key, value);
                }

                anchor.href = url.toString();
            } catch {
                //Ignore malformed links 
            }
        });
    }

    /**
     * Limits the query string to be no longer than the set maximum number of characters.
     * If the query string exceeds the length, parameters are removed until the string is below the maximum.
     * NOTE: This means that parameters at the end may be entirely removed! We do this to prevent corrupt data from being used. 
     */
    static truncateQueryParams(params) {
        const MAX_LENGTH = 2048;
        const priorityOrder = [
            'lead_source',
            'lead_description',
            'gclid',
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'utm_content'
        ];

        const finalParams = new URLSearchParams();

        const orderedKeys = [
            ...priorityOrder,
            ...Object.keys(params).filter(key => !priorityOrder.includes(key)).sort()
        ];

        //add all params initially 
        for (const key of orderedKeys) {
            if (params[key] != null && params[key] !== '') {
                finalParams.set(key, params[key]);
            }
        }

        //trim parameters if needed 
        const removalOrder = [...orderedKeys].reverse(); //remove lowest priority first 

        for (const key of removalOrder) {
            if (finalParams.toString().length <= MAX_LENGTH) break; //do nothing if under the length limit 
            finalParams.delete(key);
        }

        return finalParams;
    }
}
